import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useBilling } from "../hooks/useBilling";
import { useDashboard } from "../hooks/useDashboard";
import { useProducts } from "../hooks/useProducts";
import { PlanStatusCard } from "../components/Billing/PlanStatusCard";
import { TrialBanner } from "../components/Billing/TrialBanner";
import { TrialCountdownBanner } from "../components/Billing/TrialCountdownBanner";
import { PostTrialBanner } from "../components/Billing/PostTrialBanner";
import { ExtraPackOffer } from "../components/Billing/ExtraPackOffer";
import { SubscribeButton } from "../components/Billing/SubscribeButton";
import { WelcomeTrialModal } from "../components/Billing/WelcomeTrialModal";
import { OnboardingChecklist } from "../components/Onboarding/OnboardingChecklist";
import { getApiClient } from "../api/client";
import { hasShopContext, getShopDomain } from "../utils/shopId";
import { redirectToUrl } from "../utils/redirect";
import { useAppBridgeContext } from "../contexts/AppBridgeContext";
import { toErrorMessage } from "../utils/errorMessage";

// uid from extensions/ar-space-viewer/shopify.extension.toml — used for the
// theme-editor deep link that pre-activates the AR viewer app block.
const AR_VIEWER_EXTENSION_UID = "b6d499ea-6698-3a7a-b9d9-309a42b025296dd2a70c";

export function OverviewPage() {
  const { plan, trial, loading: billingLoading, error: billingError } = useBilling();
  const { stats, loading: statsLoading } = useDashboard();
  const { products, loading: productsLoading } = useProducts({ limit: 1 });
  const navigate = useNavigate();
  const { app } = useAppBridgeContext();
  const [syncing, setSyncing] = useState(false);
  const [syncSuccess, setSyncSuccess] = useState(false);
  const [syncError, setSyncError] = useState<string | null>(null);
  const [syncAttempted, setSyncAttempted] = useState(false);
  const [oauthNeeded, setOauthNeeded] = useState(false);

  // Helper function to check if error indicates OAuth is needed
  const isOAuthError = (err: any): boolean => {
    if (!err?.response) return false;
    
    const status = err.response.status;
    const data = err.response.data;
    const message = typeof data?.message === 'string' 
      ? data.message.toLowerCase() 
      : (typeof data?.message === 'object' && data?.message?.message 
          ? String(data.message.message).toLowerCase() 
          : '');
    
    // Check status codes that always indicate auth issues
    if (status === 401 || status === 403) {
      return true;
    }
    
    // Check for OAuth-related messages (works for 400, 404, etc.)
    const oauthKeywords = [
      'oauth',
      'authentication',
      'not authenticated',
      'complete oauth installation',
      'oauth installation',
      'shop is not authenticated',
      'access token',
      'shop not found',
      'not registered',
      'please complete oauth',
      'trigger the authentication flow',
    ];
    
    const hasOAuthKeyword = oauthKeywords.some(keyword => message.includes(keyword));
    
    // For 400/404 errors, check if message indicates OAuth is needed
    if ((status === 400 || status === 404) && hasOAuthKeyword) {
      return true;
    }
    
    // For 401/403, always return true
    if (status === 401 || status === 403) {
      return true;
    }
    
    return false;
  };

  // Helper function to extract user-friendly error message
  const extractErrorMessage = (err: any): { message: string; isOAuth: boolean } => {
    const isOAuth = isOAuthError(err);
    
    if (isOAuth) {
      return {
        message: "Please complete OAuth installation first. Access the app through Shopify Admin to trigger the authentication flow.",
        isOAuth: true,
      };
    }
    
    return {
      message: toErrorMessage(err, "Failed to sync products. Please try again later."),
      isOAuth: false,
    };
  };

  useEffect(() => {
    // Only attempt sync if:
    // 1. Products are loaded (not loading)
    // 2. Products array is empty
    // 3. Not currently syncing
    // 4. Sync hasn't succeeded
    // 5. Sync hasn't been attempted yet (prevent loops)
    // 6. Shop context is available (shop domain in URL)
    // 7. Billing has finished loading (so we can check if shop exists)
    const shouldSync = 
      !productsLoading && 
      products && 
      products.data && 
      products.data.length === 0 &&
      !syncing && 
      !syncSuccess && 
      !syncAttempted &&
      !billingLoading &&
      hasShopContext() &&
      !oauthNeeded; // Don't sync if we already know OAuth is needed

    if (shouldSync) {
      const syncProducts = async () => {
        // Mark as attempted immediately to prevent race conditions
        setSyncAttempted(true);
        
        try {
          setSyncing(true);
          setSyncError(null);
          setOauthNeeded(false);
          
          const client = getApiClient();
          await client.post("/api/products/sync");
          setSyncSuccess(true);
          window.location.reload();
        } catch (err: any) {
          console.error("Failed to sync products:", err);
          
          const { message, isOAuth } = extractErrorMessage(err);
          
          setSyncError(message);
          setOauthNeeded(isOAuth);
          setSyncing(false);
          
          // If OAuth is needed, we've already set syncAttempted=true above
          // This prevents any retries
        }
      };

      syncProducts();
    }
  }, [products, productsLoading, syncing, syncSuccess, syncAttempted, billingLoading, oauthNeeded]);

  const hasActiveSubscription =
    plan?.subscription &&
    (plan.subscription.status === "active" || plan.subscription.status === "trialing");
  const canGenerate = hasActiveSubscription || (trial?.active && !trial?.used);

  const openThemeEditor = () => {
    const shopDomain = getShopDomain();
    if (!shopDomain) return;
    // Deep link that opens the theme editor with our app embed block
    // pre-activated (Shopify's recommended onboarding pattern).
    const themeEditorUrl =
      `https://${shopDomain}/admin/themes/current/editor` +
      `?context=apps&template=product&activateAppId=${AR_VIEWER_EXTENSION_UID}/ar-space-viewer`;
    redirectToUrl(themeEditorUrl, app);
  };

  return (
    <div className="page-grid">
      {/* Welcome popup - first load after install during active trial */}
      <WelcomeTrialModal />

      {/* Trial countdown banner - shown during active trial */}
      <TrialCountdownBanner />
      
      {/* Post-trial upgrade banner - shown after trial is used */}
      <PostTrialBanner />
      
      {/* Extra AR Pack offer - shown when at ≥80% quota */}
      <ExtraPackOffer />
      
      <div className="grid gap-6 lg:grid-cols-[1.3fr_0.7fr]">
        <PlanStatusCard plan={plan} loading={billingLoading} />
        {trial && <TrialBanner trial={trial} />}
      </div>

      {/* Onboarding coaching checklist - dismissible, auto-completes from stats */}
      <OnboardingChecklist
        shopId={plan?.shop_id}
        productsWithAr={stats?.products_with_ar ?? 0}
        totalArViews={stats?.total_ar_views ?? 0}
        onOpenThemeEditor={openThemeEditor}
      />

      {stats && (
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {[{
            label: "Products with AR",
            value: stats.products_with_ar,
          },
          {
            label: "Total AR Views",
            value: stats.total_ar_views,
          },
          {
            label: "Success Rate",
            value: `${stats.generation_success_rate}%`,
          }].map((stat) => (
            <div key={stat.label} className="glass-panel">
              <p className="text-xs uppercase tracking-[0.35em] text-slate-400">{stat.label}</p>
              <p className="mt-3 text-4xl font-semibold text-slate-900">{stat.value}</p>
            </div>
          ))}
        </div>
      )}

      {statsLoading && (
        <div className="glass-panel flex items-center gap-3 text-sm text-slate-500">
          <span className="h-2 w-2 animate-ping rounded-full bg-indigo-400" />
          Loading stats...
        </div>
      )}

      {stats && (stats.failed_generations > 0 || stats.processing_generations > 0) && (
        <div className="glass-panel">
          <div className="flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
            <div>
              <span className="ghost-chip">Action required</span>
              <p className="mt-2 text-sm text-slate-500">
                Resolve any failed generations to keep your storefront pristine.
              </p>
            </div>
            <div className="flex flex-wrap gap-3">
              {stats.failed_generations > 0 && (
                <button
                  type="button"
                  onClick={() => navigate("/products?has_asset=false")}
                  className="rounded-2xl bg-gradient-to-r from-amber-400 to-rose-400 px-5 py-2.5 text-sm font-semibold text-white shadow-lg shadow-amber-200"
                >
                  Fix {String(stats.failed_generations)} failed
                </button>
              )}
              {stats.processing_generations > 0 && (
                <span className="rounded-2xl border border-slate-200 px-4 py-2 text-sm font-semibold text-slate-500">
                  {stats.processing_generations} in progress
                </span>
              )}
            </div>
          </div>
        </div>
      )}

      {!hasActiveSubscription && trial?.expired && <SubscribeButton />}

      {syncError && (
        <div className={`glass-panel border-l-4 ${oauthNeeded ? 'border-red-400 bg-red-50/50' : 'border-amber-400 bg-amber-50/50'}`}>
          <div className="flex flex-col gap-3">
            <span className="ghost-chip">{oauthNeeded ? 'OAuth Required' : 'Sync Error'}</span>
            <p className="text-sm text-slate-600">{syncError}</p>
            {oauthNeeded && (
              <div className="flex gap-2">
                <button
                  type="button"
                  onClick={() => {
                    // Redirect to backend OAuth endpoint to initiate OAuth flow
                    const shopDomain = getShopDomain();
                    if (shopDomain) {
                      // Use backend OAuth endpoint which handles the full OAuth flow.
                      // MUST use redirectToUrl (App Bridge Redirect) so the TOP-LEVEL frame navigates,
                      // not the embedded iframe — accounts.shopify.com has X-Frame-Options: deny.
                      const apiBaseUrl = import.meta.env.MODE === 'production' 
                        ? 'https://ar-space-commerce-production.up.railway.app'
                        : (import.meta.env.VITE_API_BASE_URL || 'http://localhost:3000');
                      const oauthUrl = `${apiBaseUrl}/api/auth?shop=${shopDomain}`;
                      redirectToUrl(oauthUrl, app);
                    } else {
                      // If no shop domain, refresh the page to trigger OAuth flow
                      window.location.reload();
                    }
                  }}
                  className="rounded-xl bg-gradient-to-r from-indigo-500 to-sky-500 px-4 py-2 text-sm font-semibold text-white shadow-lg transition hover:opacity-90"
                >
                  Complete OAuth Installation
                </button>
                <button
                  type="button"
                  onClick={() => {
                    setSyncError(null);
                    setOauthNeeded(false);
                    setSyncAttempted(false); // Allow retry after OAuth
                  }}
                  className="rounded-xl border border-slate-300 px-4 py-2 text-sm font-semibold text-slate-600 transition hover:bg-slate-50"
                >
                  Dismiss
                </button>
              </div>
            )}
            {syncing && !oauthNeeded && (
              <p className="text-xs text-slate-500">Retrying...</p>
            )}
          </div>
        </div>
      )}

      {syncing && !syncError && (
        <div className="glass-panel">
          <div className="flex items-center gap-3 text-sm text-slate-600">
            <span className="h-2 w-2 animate-ping rounded-full bg-indigo-400" />
            Syncing products from Shopify...
          </div>
        </div>
      )}

      {canGenerate && (
        <div className="glass-panel">
          <div className="flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
            <div>
              <span className="ghost-chip">Get started</span>
              <h3 className="mt-3 text-xl font-semibold text-slate-900">
                Generate your next AR set in minutes
              </h3>
              <p className="text-sm text-slate-500">
                Sync products, review settings, and trigger 3D output in one flow.
              </p>
            </div>
            <button
              type="button"
              onClick={() => navigate("/products")}
              className="w-full rounded-2xl bg-gradient-to-r from-indigo-500 to-sky-500 px-6 py-3 text-sm font-semibold text-white shadow-xl shadow-indigo-200 transition hover:opacity-90 md:w-auto"
            >
              Go to products
            </button>
          </div>
        </div>
      )}

      <div className="glass-panel">
        <div className="flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
          <div>
            <span className="ghost-chip">Storefront setup</span>
            <h3 className="mt-3 text-xl font-semibold text-slate-900">
              Enable the AR viewer on your product pages
            </h3>
            <p className="text-sm text-slate-500">
              Add the AR Space Viewer block to your product template in the theme editor —
              one click, no code. Shoppers then see the &quot;View in AR&quot; experience
              on products with a generated 3D model.
            </p>
          </div>
          <button
            type="button"
            onClick={openThemeEditor}
            className="w-full rounded-2xl bg-gradient-to-r from-emerald-500 to-teal-500 px-6 py-3 text-sm font-semibold text-white shadow-xl shadow-emerald-200 transition hover:opacity-90 md:w-auto"
          >
            Open theme editor
          </button>
        </div>
      </div>

      <div className="glass-panel">
        <h3 className="text-lg font-semibold text-slate-900">How it works</h3>
        <div className="mt-4 grid gap-4 md:grid-cols-2">
          {[
            "Select Shopify products you'd like in AR",
            "Hit Generate to send references to the 3D pipeline",
            "Automatically sync resulting GLBs + thumbnails",
            "Drop the AR block on your storefront and track views",
          ].map((step, index) => (
            <div key={step} className="rounded-2xl border border-slate-100 bg-white/60 p-4">
              <p className="text-xs font-semibold uppercase tracking-[0.4em] text-slate-400">
                {String(index + 1).padStart(2, "0")}
              </p>
              <p className="mt-2 text-sm text-slate-600">{step}</p>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
